import type { ReactNode } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';

export interface OnboardingStepFrameErrorState {
  title: string;
  description: ReactNode;
  action?: ReactNode;
}

export interface OnboardingStepFrameProps {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  isLoading?: boolean;
  error?: OnboardingStepFrameErrorState;
}

export function OnboardingStepFrame(props: OnboardingStepFrameProps) {
  const {
    title,
    description,
    children,
    primaryAction,
    secondaryAction,
    isLoading = false,
    error,
  } = props;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{error.title}</AlertTitle>
        <AlertDescription>
          <div className="flex flex-col gap-2">
            <div>{error.description}</div>
            {error.action ? <div>{error.action}</div> : null}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="flex h-full min-h-0 flex-col">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>

      <CardContent className="min-h-0 flex-1">{isLoading ? null : children}</CardContent>

      {primaryAction || secondaryAction ? (
        <CardFooter className="mt-auto flex flex-col gap-2 sm:flex-row sm:justify-end">
          {secondaryAction}
          {primaryAction}
        </CardFooter>
      ) : null}
    </Card>
  );
}
